<template>
  <main>
    <header :class="style.returnAuthorizationBlock">
      <router-link to="authorization" :class="style.button">
        <GhostButton size="M" :icon="closeIcon" />
      </router-link>
    </header>

    <ToastError ref="toastError" :text="'Неверный логин или пароль'" />

    <form novalidate @submit.prevent="registrationUser">
      <h1>Регистрация аккаунта</h1>

      <div :class="style.mainForm">
        <div :class="style.inputBlock">
          <InputStyle
            placeholder="Введите имя"
            description="Имя"
            size="M"
            errorText="Введите имя"
            :isError="errors.errorName"
            :disabled="isSubmitting"
            v-model="registrationData.name"
          />

          <InputStyle
            placeholder="example@mail.com"
            description="Email"
            size="M"
            type="email"
            errorText="Укажите корректный email"
            :isError="errors.errorEmail"
            :disabled="isSubmitting"
            v-model="registrationData.email"
          />

          <InputStyle
            placeholder="+7 999 123-45-67"
            description="Телефон"
            size="M"
            type="tel"
            errorText="Укажите корректный номер телефона"
            :isError="errors.errorPhone"
            :disabled="isSubmitting"
            v-model="registrationData.phone"
          />

          <InputStyle
            placeholder="Придумайте пароль"
            description="Пароль"
            size="M"
            type="password"
            errorText="Придумайте пароль"
            :isError="errors.errorPassword"
            :disabled="isSubmitting"
            v-model="registrationData.password"
          >
            <template #actions="{togglePassword, isPasswordVisible}">
              <ButtonIcon
                :icon="isPasswordVisible ? eyeOff : eyeOn"
                :disabled="isSubmitting"
                @click.stop.prevent="togglePassword"
              />
            </template>
          </InputStyle>

          <InputStyle
            placeholder="Повторите пароль"
            description="Повторите пароль"
            size="M"
            type="password"
            errorText="Пароли не совпадают"
            :isError="errors.errorProofPassword"
            :disabled="isSubmitting"
            v-model="registrationData.proofPassword"
          >
            <template #actions="{togglePassword, isPasswordVisible}">
              <ButtonIcon
                :icon="isPasswordVisible ? eyeOff : eyeOn"
                :disabled="isSubmitting"
                @click.stop.prevent="togglePassword"
              />
            </template>
          </InputStyle>
        </div>
      </div>

      <PrimaryButton
        text="Зарегистрироваться"
        type="submit"
        :class="style.mainButton"
        :disabled="isSubmitting"
      />
    </form>
  </main>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import style from "../../StartPage.module.scss";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import ToastError from "@/elements/UI/overlay/Toast/Error/ToastError.vue";
import closeIcon from "$images/icons/button/Name=Close, Size=16, Tone=Default.svg";
import eyeOn from "$images/icons/input/Name=Eye, Size=16, Tone=Default.svg";
import eyeOff from "$images/icons/input/Name=Eye-off, Size=16, Tone=Default.svg";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import {API} from "@/api/generated";

type RegistrationData = {
  name: string;
  phone: string;
  email: string;
  password: string;
  proofPassword: string;
  utm: string;
};

type RegistrationErrors = {
  errorName: boolean;
  errorEmail: boolean;
  errorPhone: boolean;
  errorPassword: boolean;
  errorProofPassword: boolean;
};

export default defineComponent({
  name: "RegistrationPage",
  components: {
    ButtonIcon,
    GhostButton,
    ToastError,
    InputStyle,
    PrimaryButton
  },
  data() {
    return {
      style,
      closeIcon,
      eyeOn,
      eyeOff,
      errors: {
        errorName: false,
        errorEmail: false,
        errorPhone: false,
        errorPassword: false,
        errorProofPassword: false
      } as RegistrationErrors,
      registrationData: {
        name: "",
        phone: "",
        email: "",
        password: "",
        proofPassword: "",
        utm: ""
      } as RegistrationData,
      isSubmitting: false
    };
  },
  created() {
    localStorage.clear();
  },
  computed: {
    popupRef(): ToastType | null | unknown {
      return (this.$refs.toastError as ToastType | unknown) ?? null;
    }
  },
  methods: {
    async clearCaches(): Promise<void> {
      try {
        if (!("caches" in window)) return;
        const keys = await window.caches.keys();
        await Promise.all(keys.map((key) => window.caches.delete(key)));
      } catch {
        // Cache Storage is optional; its availability must not block registration.
      }
    },

    showToastError(): void {
      const popup = this.popupRef as ToastType;

      if (popup && typeof popup.show === "function") {
        popup.show();
      }
    },

    parseUtmFromQuery(): string {
      const searchParams = new URLSearchParams(window.location.search);
      let result = "";

      searchParams.forEach((value, key) => {
        if (key.substring(0, 3).toLowerCase() === "utm") {
          result += `${key}: ${value}\n`;
        }
      });

      return result;
    },

    async registrationUser(): Promise<void> {
      if (this.isSubmitting) {
        return;
      }

      this.registrationData.utm = this.parseUtmFromQuery();

      if (!this.validationForm()) {
        this.showToastError();
        return;
      }

      this.isSubmitting = true;
      let shouldKeepSubmitting = false;

      try {
        const data = await API.registrationUser({
          body: {
            ...this.registrationData,
            email: this.registrationData.email.trim(),
            name: this.registrationData.name.trim()
          }
        });

        if (data?.email) {
          await this.clearCaches();
          await this.$router.push({
            name: "registrationConfirm",
            query: {email: data.email}
          });
          shouldKeepSubmitting = true;
        } else {
          this.showToastError();
        }
      } catch {
        this.showToastError();
      } finally {
        if (!shouldKeepSubmitting) {
          this.isSubmitting = false;
        }
      }
    },

    resetErrors(): void {
      this.errors = {
        errorName: false,
        errorEmail: false,
        errorPhone: false,
        errorPassword: false,
        errorProofPassword: false
      };
    },

    validationForm(): boolean {
      this.resetErrors();

      if (!this.registrationData.name.trim()) {
        this.errors.errorName = true;
      }

      if (
        !this.registrationData.email.trim() ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.registrationData.email.trim())
      ) {
        this.errors.errorEmail = true;
      }

      const phoneDigits = this.registrationData.phone.replace(/\D/g, "");

      if (phoneDigits.length !== 11) {
        this.errors.errorPhone = true;
      }

      if (!this.registrationData.password) {
        this.errors.errorPassword = true;
      }

      if (
        !this.registrationData.proofPassword ||
        this.registrationData.proofPassword !== this.registrationData.password
      ) {
        this.errors.errorProofPassword = true;
      }

      return !Object.values(this.errors).some((error) => error);
    }
  },
  watch: {
    registrationData: {
      handler(): void {
        this.resetErrors();
      },
      deep: true
    }
  }
});
</script>
